import { ManagedLeadDropRequest } from '../../services/api/managed-leads/managedLeads';
import {
  classifyTassoQuintoError,
  getTassoQuintoErrorCode,
  getTassoQuintoErrorDetails,
} from '../../services/api/tasso-quinto';
import {
  MANAGED_LEAD_AUTH_UNAVAILABLE_MESSAGE,
  MANAGED_LEAD_DEAL_NOT_FOUND_MESSAGE,
  MANAGED_LEAD_OWNER_MISSING_MESSAGE,
  MANAGED_LEAD_SESSION_EXPIRED_MESSAGE,
  readManagedLeadRejectedFields,
} from './managedLeadFailures';

/**
 * What a failed Drop request means for the form. Unlike an update, a Drop
 * carries no idempotency key: the server deduplicates by product and Deal, so
 * the only thing a retry can burn is the operator's patience.
 */
export interface ManagedLeadDropFailure {
  message: string;
  /**
   * A refused duplicate is not a breakage: the lead already has the request the
   * operator wanted, so it is announced rather than reported as an error.
   */
  severity: 'error' | 'warning';
  /** Pressing Riprova with the same reason and text can still succeed. */
  retryable: boolean;
  /** The Drop metadata driving the form are stale and must be re-read. */
  refetchesDetail: boolean;
  /** The session, not the request, failed: send the user to the login page. */
  sessionExpired: boolean;
  noteFieldMessage: string | null;
  reasonFieldMessage: string | null;
  /**
   * The request already awaiting review, when the server named it while
   * refusing a second one. The detail carries the same request once the refetch
   * lands; this is what puts it on screen if that call is slow or fails.
   */
  pendingDrop: ManagedLeadDropRequest | null;
}

const RETRYABLE_MESSAGE =
  'Non è stato possibile inviare la richiesta di Drop. I dati inseriti sono stati conservati.';

const FIELD_LABELS: Record<string, string> = {
  reason: 'Motivo',
  note: 'Nota',
};

const baseFailure: ManagedLeadDropFailure = {
  message: RETRYABLE_MESSAGE,
  severity: 'error',
  retryable: true,
  refetchesDetail: false,
  sessionExpired: false,
  noteFieldMessage: null,
  reasonFieldMessage: null,
  pendingDrop: null,
};

const invalidRequestFailure = (error: unknown): ManagedLeadDropFailure => {
  const { names, labels } = readManagedLeadRejectedFields(error, FIELD_LABELS);

  return {
    ...baseFailure,
    message: labels.length
      ? `La richiesta di Drop non è valida: controlla ${labels.join(', ')}.`
      : 'La richiesta di Drop non è valida: controlla i dati inseriti.',
    retryable: false,
    noteFieldMessage: names.includes('note')
      ? 'La nota non rispetta i limiti previsti.'
      : null,
    reasonFieldMessage: names.includes('reason')
      ? 'Scegli uno dei motivi disponibili.'
      : null,
  };
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * Reads the pending request out of `details.dropRequest`. Only the identity is
 * demanded: a request whose optional metadata the server left out is still the
 * request the operator has to be told about.
 */
const readPendingDrop = (error: unknown): ManagedLeadDropRequest | null => {
  const candidate = getTassoQuintoErrorDetails(error).dropRequest;
  if (!isRecord(candidate) || typeof candidate.id !== 'string') return null;

  const reason = candidate.reason;
  return {
    id: candidate.id,
    status: typeof candidate.status === 'string' ? candidate.status : 'pending',
    reason:
      isRecord(reason) && typeof reason.label === 'string'
        ? {
            key: typeof reason.key === 'string' ? reason.key : '',
            label: reason.label,
          }
        : null,
    stageAtRequest:
      typeof candidate.stageAtRequest === 'string'
        ? candidate.stageAtRequest
        : '',
    requestedAt:
      typeof candidate.requestedAt === 'string' ? candidate.requestedAt : null,
    cancellable: candidate.cancellable === true,
  };
};

/**
 * The answer for a response this module has no `code` case for, decided by the
 * read path's classifier so that every managed lead page reads an uncoded or
 * newly minted response the same way.
 */
const classifiedFailure = (error: unknown): ManagedLeadDropFailure => {
  switch (classifyTassoQuintoError(error)) {
    case 'session_expired':
      return {
        ...baseFailure,
        message: MANAGED_LEAD_SESSION_EXPIRED_MESSAGE,
        severity: 'warning',
        retryable: false,
        sessionExpired: true,
      };
    case 'auth_unavailable':
      return { ...baseFailure, message: MANAGED_LEAD_AUTH_UNAVAILABLE_MESSAGE };
    case 'unauthorized':
      return {
        ...baseFailure,
        message: 'Non sei autorizzato a richiedere il Drop di questa lead.',
        retryable: false,
      };
    case 'not_found':
      return {
        ...baseFailure,
        message: MANAGED_LEAD_DEAL_NOT_FOUND_MESSAGE,
        retryable: false,
      };
    case 'owner_missing':
      return {
        ...baseFailure,
        message: MANAGED_LEAD_OWNER_MISSING_MESSAGE,
        retryable: false,
      };
    default:
      return baseFailure;
  }
};

/**
 * Translates a Drop failure into what the operator sees and what the form does
 * next, reacting to the stable envelope `code` and never to its message.
 */
export const describeManagedLeadDropFailure = (
  error: unknown
): ManagedLeadDropFailure => {
  if (classifyTassoQuintoError(error) === 'session_expired') {
    return classifiedFailure(error);
  }

  switch (getTassoQuintoErrorCode(error)) {
    case 'authentication_unavailable':
      return {
        ...baseFailure,
        message: MANAGED_LEAD_AUTH_UNAVAILABLE_MESSAGE,
      };
    case 'insufficient_scope':
      return {
        ...baseFailure,
        message: 'Non sei autorizzato a richiedere il Drop di questa lead.',
        retryable: false,
      };
    case 'drop_not_allowed':
      return {
        ...baseFailure,
        message:
          'Il Drop non è più disponibile per la fase corrente. Il dettaglio è stato ricaricato.',
        retryable: false,
        refetchesDetail: true,
      };
    case 'deal_not_found':
      return {
        ...baseFailure,
        message: MANAGED_LEAD_DEAL_NOT_FOUND_MESSAGE,
        retryable: false,
      };
    case 'drop_request_pending':
      return {
        ...baseFailure,
        message:
          'Esiste già una richiesta di Drop in attesa di revisione per questa lead: non ne è stata inviata una seconda.',
        severity: 'warning',
        // A second request is not a failed one: nothing is left to retry, and
        // the pending request the server named is now on screen.
        retryable: false,
        refetchesDetail: true,
        // The server sends the request it refused a duplicate of precisely so
        // it can be shown: the badge must not wait on the refetch to succeed.
        pendingDrop: readPendingDrop(error),
      };
    case 'drop_request_in_progress':
      return {
        ...baseFailure,
        message:
          'Una richiesta di Drop per questa lead è ancora in corso di invio. Attendi qualche istante e riprova.',
      };
    case 'invalid_request':
      return invalidRequestFailure(error);
    default:
      return classifiedFailure(error);
  }
};

export const MANAGED_LEAD_PENDING_DROP_LABEL = 'Drop richiesto';

export const MANAGED_LEAD_DROP_SENT_MESSAGE =
  'Richiesta di Drop inviata. Fino alla revisione amministrativa, la lead è disponibile in sola lettura.';

/**
 * How a pending request reads on the lead it sits on. The reason is named when
 * the server sent one, and the sentence always says what the operator can and
 * cannot do next: wait, view the lead, and not withdraw the request.
 */
export const describeManagedLeadPendingDrop = (
  pendingDrop: ManagedLeadDropRequest
) => {
  const reason = pendingDrop.reason?.label;
  return `Richiesta di Drop in attesa di revisione${
    reason ? ` per il motivo "${reason}"` : ''
  }. La lead resta nella fase corrente, è disponibile in sola lettura e la richiesta non può essere annullata.`;
};
